import i18next from 'i18next';
import { Instance, SnapshotOut, types } from 'mobx-state-tree';
import { setAnalyticsUser, trackEvent } from 'src/analytics';
import { LANGUAGE } from 'src/assets/static/constants';
import { sendWidgetData } from 'src/hooks/useWebViewCommunication';
import { ILoginData, IRegister } from 'src/screens';
import { IPasswordChangeRequest, IPasswordResetRequest } from 'src/screens/auth/types';
import { authApi } from 'src/services';

import { withRootStore } from '../extensions/withRootStore';
import { withSetPropAction } from '../extensions/withSetPropAction';
import { User } from '../user/User';

/**
 * # AuthStore
 */
export const AuthStore = types
  .model('AuthStore')
  .props({
    isAuthenticated: types.optional(types.boolean, false),
    isInitialized: types.optional(types.boolean, false),
    user: types.optional(types.maybeNull(User), null),
    isSaved: types.optional(types.boolean, false),
  })
  .extend(withRootStore)
  .actions(withSetPropAction)
  .actions((_self) => ({
    getAccessToken() {
      return localStorage.getItem('accessToken');
    },
    setAccessToken(token: string) {
      localStorage.setItem('accessToken', token);
    },
    getRefreshToken() {
      return localStorage.getItem('refreshToken');
    },
    setRefreshToken(token: string) {
      localStorage.setItem('refreshToken', token);
    },
  }))
  .actions((_self) => ({
    initAuth() {
      _self.setProp('isAuthenticated', false);
      // 토큰을 완전히 삭제 (빈 문자열이 아닌 키 자체 제거)
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // 언어 설정은 유지 (로그아웃 후에도 선택한 언어로 표시)
    },
  }))
  .actions((self) => {
    /** 로그인 */
    const login = async (loginData: ILoginData) => {
      try {
        const result = await authApi.login(loginData);
        if (result?.resultCode === 'S' && result.data) {
          const oauthToken = result.data;
          if (oauthToken) {
            self.setAccessToken(oauthToken?.access_token);
            self.setRefreshToken(oauthToken?.refresh_token);
          }
          trackEvent('auth_login', { method: 'password', success: true });
        } else {
          self.initAuth();
          trackEvent('auth_login', {
            method: 'password',
            success: false,
            error_code: result?.resultCode ?? 'unknown',
          });
        }
        return result;
      } catch (error) {
        self.initAuth();
        trackEvent('auth_login', { method: 'password', success: false, error_code: 'exception' });
      }
    };

    /** 로그아웃 */
    const logout = async () => {
      try {
        const result = await authApi.logout();
        if (result?.resultCode === 'S') {
          self.initAuth();
          self.setProp('user', null);
          trackEvent('auth_logout');
          setAnalyticsUser(null);
          // 언어 설정은 유지 (로그아웃 후에도 선택한 언어로 표시)
          // 위젯 데이터 갱신 (로그아웃 상태)
          sendWidgetData({
            isLoggedIn: false,
          });
        }
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 내 정보 조회 */
    const getUserProfile = async () => {
      try {
        const result = await authApi.getUserProfile();
        if (result?.resultCode === 'S' && result.data) {
          const userProfile = result.data;
          if (userProfile) {
            self.setProp('user', userProfile);
            self.setProp('isAuthenticated', true);
            i18next.changeLanguage(userProfile.langCd || LANGUAGE.korean);
            setAnalyticsUser({
              user_id: String(userProfile.userSid),
              quit_date: userProfile.quitDt ?? undefined,
              language: userProfile.langCd ?? undefined,
              gender: userProfile.genderTypeCd?.value ?? undefined,
              hospital_name: userProfile.hsptlInfo?.hsptlNm ?? undefined,
              ftnd_completed: userProfile.ftndCompleteYn === 'Y',
            });
          }
        } else {
          self.initAuth();
        }
        self.setProp('isInitialized', true);
        return result;
      } catch (error) {
        self.initAuth();
      }
    };

    /** 아이디 중복 확인 */
    const checkDuplicateLoginId = async (loginId: string) => {
      try {
        const result = await authApi.checkDuplicateLoginId(loginId);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 아이디 찾기 */
    const findLoginId = async (verifyKey: string) => {
      try {
        const result = await authApi.findLoginId(verifyKey);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 비밀번호 재설정(로그인 이전) */
    const resetPwd = async (data: IPasswordResetRequest) => {
      try {
        const result = await authApi.resetPwd(data);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 토큰 재발급 */
    const loginRefresh = async (refreshToken: string) => {
      try {
        const result = await authApi.loginRefresh(refreshToken);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 비밀번호 변경(로그인 이후) */
    const changePwd = async (data: IPasswordChangeRequest) => {
      try {
        const result = await authApi.changePwd(data);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 회원가입 */
    const signup = async (formData: IRegister) => {
      try {
        const result = await authApi.signup(formData);
        if (result?.resultCode === 'S' && result.data) {
          const oauthToken = result.data;
          if (oauthToken) {
            self.setAccessToken(oauthToken?.access_token);
            self.setRefreshToken(oauthToken?.refresh_token);
          }
          trackEvent('auth_signup_complete');
        } else {
          self.initAuth();
        }
        return result;
      } catch (error) {
        self.initAuth();
      }
    };

    /** 언어 변경 */
    const changeLanguage = async (langCd: string) => {
      try {
        const result = await authApi.changeLanguage(langCd, self.getRefreshToken());
        if (result?.resultCode === 'S' && result.data) {
          const oauthToken = result.data;
          if (oauthToken) {
            self.setAccessToken(oauthToken?.access_token);
            self.setRefreshToken(oauthToken?.refresh_token);
          }
        } else {
          self.initAuth();
        }
        return result;
      } catch (error) {
        self.initAuth();
      }
    };

    /** 회원탈퇴 */
    const withdrawal = async () => {
      try {
        const result = await authApi.withdrawal();
        if (result?.resultCode === 'S') {
          self.initAuth();
          self.setProp('user', null);
          // 언어 설정은 유지 (탈퇴 후에도 선택한 언어로 표시)
        }
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    return {
      login,
      logout,
      getUserProfile,
      checkDuplicateLoginId,
      findLoginId,
      resetPwd,
      loginRefresh,
      changePwd,
      signup,
      changeLanguage,
      withdrawal,
    };
  });

type TAuthStore = Instance<typeof AuthStore>;
type TAuthStoreSnapshot = SnapshotOut<typeof AuthStore>;
export interface IAuthStore extends TAuthStore {}
export type TAuthStoreKeys = keyof TAuthStoreSnapshot & string;
export interface IAuthStoreSnapshot extends TAuthStoreSnapshot {}
